import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Save, 
  X, 
  AlertTriangle, 
  CheckCircle, 
  Package, 
  DollarSign, 
  Edit3,
  Eye,
  RefreshCw,
  Calculator,
  FileText,
  User,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getChargebackLineItems,
  updateChargebackLineItem,
  validateChargebackLineItem,
  getContractPricing
} from '../../config/api';

const ChargebackLineItemEditor = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [editingItem, setEditingItem] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Fetch line items
  const { data: lineItemsData, isLoading, error, refetch } = useQuery({
    queryKey: ['chargeback-line-items', claimId],
    queryFn: () => getChargebackLineItems(claimId),
    enabled: !!claimId && !!user
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: updateChargebackLineItem,
    onSuccess: () => {
      toast.success('Line item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback-line-items', claimId] });
      setEditingItem(null);
      setValidationErrors({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update line item');
    }
  });

  const validateMutation = useMutation({
    mutationFn: validateChargebackLineItem,
    onSuccess: (data) => {
      setValidationErrors(data.errors || {});
      setShowValidation(true);
      if (data.isValid) {
        toast.success('Line item validation passed');
      } else {
        toast.error('Line item validation failed');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Validation failed');
    }
  });

  // Handlers
  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setValidationErrors({});
    setShowValidation(false);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setValidationErrors({});
    setShowValidation(false);
  };

  const handleSave = () => {
    if (!editingItem) return;

    // Validate required fields
    const errors = {};
    if (!editingItem.quantity || editingItem.quantity <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }
    if (!editingItem.salesPrice || editingItem.salesPrice <= 0) {
      errors.salesPrice = 'Sales price must be greater than 0';
    }
    if (!editingItem.contractPrice || editingItem.contractPrice <= 0) {
      errors.contractPrice = 'Contract price must be greater than 0';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error('Please fix validation errors');
      return;
    }

    updateMutation.mutate({
      lineItemId: editingItem.id,
      data: {
        quantity: editingItem.quantity,
        salesPrice: editingItem.salesPrice,
        contractPrice: editingItem.contractPrice,
        validatedPrice: editingItem.validatedPrice,
        notes: editingItem.notes,
        editedBy: user.id,
        editedAt: new Date().toISOString()
      }
    });
  };

  const handleValidate = (item) => {
    validateMutation.mutate({
      lineItemId: item.id,
      data: {
        quantity: item.quantity,
        salesPrice: item.salesPrice,
        contractPrice: item.contractPrice,
        productId: item.productId,
        customerId: item.claim?.customerId
      }
    });
  };

  const handleFieldChange = (field, value) => {
    if (!editingItem) return;
    
    setEditingItem(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VALID': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'INVALID': return <X className="h-4 w-4 text-red-500" />;
      case 'PENDING': return <RefreshCw className="h-4 w-4 text-yellow-500" />;
      case 'NEEDS_REVIEW': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default: return <RefreshCw className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID': return 'bg-green-100 text-green-800';
      case 'INVALID': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'NEEDS_REVIEW': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredItems = lineItemsData?.lineItems?.filter(item => {
    const matchesSearch = !searchTerm || 
      item.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.product?.ndc11?.includes(searchTerm);
    
    const matchesStatus = filterStatus === 'ALL' || item.validationStatus === filterStatus;
    
    return matchesSearch && matchesStatus;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading line items</h3>
        <p className="mt-1 text-sm text-gray-500">
          {error.response?.data?.message || 'Failed to load line items. Please try again.'}
        </p>
        <button
          onClick={() => refetch()}
          title="Try again"
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            title="Go back"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Line Item Editor</h1>
            <p className="text-gray-600">Edit and validate chargeback line items</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => refetch()}
            title="Refresh line items"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name or NDC..."
                className="border border-gray-300 rounded-md px-3 py-1 text-sm w-64"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="VALID">Valid</option>
                <option value="INVALID">Invalid</option>
                <option value="NEEDS_REVIEW">Needs Review</option>
              </select>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            {filteredItems.length} of {lineItemsData?.lineItems?.length || 0} items
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.product?.name || 'Unknown Product'}
                        </div>
                        <div className="text-sm text-gray-500">
                          NDC: {item.product?.ndc11 || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <div>
                        <input
                          type="number"
                          value={editingItem.quantity}
                          onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value))}
                          className={`border rounded-md px-2 py-1 text-sm w-20 ${
                            validationErrors.quantity ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {validationErrors.quantity && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.quantity}</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900">{item.quantity}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <div>
                        <input
                          type="number"
                          step="0.01"
                          value={editingItem.salesPrice}
                          onChange={(e) => handleFieldChange('salesPrice', parseFloat(e.target.value))}
                          className={`border rounded-md px-2 py-1 text-sm w-24 ${
                            validationErrors.salesPrice ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {validationErrors.salesPrice && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.salesPrice}</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900">{item.salesPrice != null ? `$${Number(item.salesPrice).toFixed(2)}` : 'N/A'}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <div>
                        <input
                          type="number"
                          step="0.01"
                          value={editingItem.contractPrice}
                          onChange={(e) => handleFieldChange('contractPrice', parseFloat(e.target.value))}
                          className={`border rounded-md px-2 py-1 text-sm w-24 ${
                            validationErrors.contractPrice ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {validationErrors.contractPrice && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.contractPrice}</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900">{item.contractPrice != null ? `$${Number(item.contractPrice).toFixed(2)}` : 'N/A'}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.variance != null ? `$${Number(item.variance).toFixed(2)}` : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.validationStatus)}`}>
                      {getStatusIcon(item.validationStatus)}
                      <span className="ml-1">{item.validationStatus}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingItem?.id === item.id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                          title="Save"
                          className="text-green-600 hover:text-green-900"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancel}
                          title="Cancel"
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(item)}
                          title="Edit"
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleValidate(item)}
                          disabled={validateMutation.isPending}
                          title="Validate"
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Calculator className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No line items found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No line items match your current filters.
            </p>
          </div>
        )}
      </div>

      {/* Validation Results Modal */}
      {showValidation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Validation Results</h3>
                <button
                  onClick={() => setShowValidation(false)}
                  title="Close"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {Object.keys(validationErrors).length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                  <p className="mt-2 text-sm text-gray-900">Validation passed successfully!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">The following issues were found:</p>
                  {Object.entries(validationErrors).map(([field, error]) => (
                    <div key={field} className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-600">{error}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowValidation(false)}
                  title="Close"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargebackLineItemEditor;
